/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core'
import { takeUntil, finalize } from 'rxjs/operators'
import { Observable, Subject } from 'rxjs'
import { TranslateService } from '@ngx-translate/core'

import { NotificationMessage } from '../notification-message.model'
import { NotificationService } from '../notification.service'
import { SocketService } from '@core/services/socket.service'
import { UtilityService } from '@core/services/utility.service'
import { NotificationDataSource } from './notification.data-source'
import { PagesData } from '@shared/models/pages-data.model'
import { SnackBarService } from '@core/modules/snack-bar/snack-bar.service'
import { SettingsService } from '@app/shared/common-components/settings/settings.service'
import { AuthService } from '@app/modules/auth/auth.service'
import { Router } from '@angular/router'
import { FeatureFlagService } from '@core/services/feature-flag.service'
import { RealtimeAlertService } from '../realtime-alert.service'

@Component({
  selector: 'lin-notification-list',
  templateUrl: './notification-list.component.html',
  styleUrls: ['./notification-list.component.scss'],
  standalone: false,
})
export class NotificationListComponent implements OnInit, OnDestroy {
  markedAsRead: { [id: string]: boolean } = {}
  isLoaded = false

  private page = 1
  private pageSize = 5
  private total: number
  private dataSource: NotificationDataSource
  private audio: HTMLAudioElement | null = null
  user = this.authService.getCurrentUser()

  private readonly onDestroy$ = new Subject<void>()

  notifications: NotificationMessage[] = []

  @Input() isNotificationSound: boolean
  @Input() isNotificationPopup: boolean

  @Output() close = new EventEmitter<void>()
  @Output() markRead = new EventEmitter<string>()
  @Output() markAllRead = new EventEmitter<void>()
  @Output() notificationArrived = new EventEmitter<NotificationMessage[]>()

  @ViewChild(TemplateRef) templateRef: TemplateRef<any>

  constructor(
    private readonly notificationService: NotificationService,
    private readonly socketService: SocketService,
    private readonly snackBarService: SnackBarService,
    private readonly translateService: TranslateService,
    private readonly utilityService: UtilityService,
    private readonly settingsService: SettingsService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly featureFlagService: FeatureFlagService,
    private readonly realtimeAlertService: RealtimeAlertService,
  ) {}

  ngOnInit(): void {
    this.initDataSource()
  }

  loadNotifications(): void {
    // A realtime WS alert can populate `notifications` (and bump the badge) before the panel is
    // ever opened. When it has, just show what we have and flip the spinner off — the original
    // guard skipped the fetch but left `isLoaded` false, hanging on "Loading" until a page reload.
    // Only fetch the initial page when there is nothing to show yet; fetching on top of a WS item
    // would append the API copy of the same alert and render it twice (the WS id is a string, the
    // API id a number, so a plain de-dupe would miss it).
    if (this.notifications.length) {
      this.isLoaded = true
      return
    }

    this.fetchNotifications()
  }

  private lazyInitAudio(): void {
    if (this.isMapPage()) return
    if (this.audio) return

    this.audio = new Audio()
    this.audio.src = '/assets/audio/juntos-607.mp3'
    this.audio.load()

    this.settingsService
      .getUserSoundVolumeSetting(this.user.id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((setting) => {
        if (setting?.value === 'Loud') {
          this.audio!.src = '/assets/audio/mixkit-interface-option-select-2573.wav'
          this.audio!.volume = 1.0
        } else {
          this.audio!.src = '/assets/audio/juntos-607.mp3'
          this.audio!.volume = 0.3
        }

        this.audio!.load()
      })
  }

  private showNotification(notifications: NotificationMessage[]): void {
    const notificationsText: string = this.translateService.instant('FIELDS.notifications')
    const firstElementText: string = notifications[0].subject
    const arrayLength: number = notifications.length
    const message: string = arrayLength === 1 ? firstElementText : `${firstElementText} + ${arrayLength - 1} ${notificationsText}`

    this.snackBarService.notificationArrived(message)
  }

  private fetchNotifications(): void {
    this.isLoaded = false

    this.notificationService
      .getList(1, this.pageSize)
      .pipe(
        finalize(() => (this.isLoaded = true)),
        takeUntil(this.onDestroy$),
      )
      .subscribe(({ data, meta }: PagesData<NotificationMessage>) => {
        const unreadNotifications = data.filter((notification) => !notification.isRead)
        this.notifications = [...this.notifications, ...unreadNotifications]
        this.total = meta.total
      })
  }

  private initDataSource(): void {
    this.dataSource = new NotificationDataSource(
      this.notificationService,
      this.socketService,
      this.utilityService,
      this.featureFlagService,
      this.realtimeAlertService,
    )

    this.dataSource
      .connect()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((notifications) => {
        const newNotifs = notifications.filter((n) => !n.isRead)
        this.notifications = [...newNotifs, ...this.notifications]

        if (this.isNotificationPopup) {
          this.showNotification(newNotifs)
        }

        if (this.isNotificationSound && !this.isMapPage()) {
          this.lazyInitAudio()
          this.audio?.play()
        }

        this.notificationArrived.next(newNotifs)
      })
  }

  scrollHandler(): void {
    if (this.page * this.pageSize >= this.total || !this.isLoaded) return
    this.page = this.page + 1
    this.fetchNotifications()
  }

  emitMarkRead(id: string): void {
    this.markRead.emit(id)
    this.notifications = this.notifications.filter((notification) => notification.id !== id)
  }

  markAllAsRead(): void {
    this.notifications.forEach(({ id }: NotificationMessage) => (this.markedAsRead[id] = true))
    this.markAllRead.emit()
  }

  trackById(_, { id }: NotificationMessage): string {
    return id
  }

  private isMapPage(): boolean {
    return this.router.url.includes('/client/map')
  }

  ngOnDestroy(): void {
    this.dataSource.disconnect()
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }
}
